import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { css } from '@terra/ui-styled-system';

export type SnapOrClampPaneOptions = {
  paneIndex: number;
  contentIndex: number;
  minWidthPx: number;
  maxWidthPx?: number;
  minContentWidthPx?: number;
  collapsedWidthPx: number;
  snapThresholdPx: number;
  containerWidth: number;
  skipSnap?: boolean;
};

export type UseCollapsiblePaneConfig = {
  paneId: string;
  contentId: string;
  panePosition?: 'start' | 'end';
  minWidthPx: number;
  maxWidthPx?: number;
  minContentWidthPx?: number;
  preferredPaneWidthPx?: number;
  collapsedWidthPx: number;
  snapThresholdPx: number;
  collapseOnSnap?: boolean;
  persistedSize: number;
  isCollapsed: boolean;
  onCollapseChange: (collapsed: boolean) => void;
  onResizeEnd: (
    paneSize: number,
    contentSize: number,
    shouldCollapse: boolean,
  ) => void;
};

const FALLBACK_CONTAINER_WIDTH_PX = 1800;

function pxToPercent(px: number, containerWidth: number) {
  return (px / containerWidth) * 100;
}

function percentToPx(pct: number, containerWidth: number) {
  return (pct / 100) * containerWidth;
}

export function snapOrClampPane(sizes: number[], opts: SnapOrClampPaneOptions) {
  const panePx = percentToPx(sizes[opts.paneIndex], opts.containerWidth);

  if (!opts.skipSnap && panePx < opts.snapThresholdPx) {
    const collapsedPercent = pxToPercent(
      opts.collapsedWidthPx,
      opts.containerWidth,
    );

    sizes[opts.contentIndex] += sizes[opts.paneIndex] - collapsedPercent;
    sizes[opts.paneIndex] = collapsedPercent;

    return;
  }

  if (panePx < opts.minWidthPx) {
    const minPercent = pxToPercent(opts.minWidthPx, opts.containerWidth);

    sizes[opts.contentIndex] -= minPercent - sizes[opts.paneIndex];
    sizes[opts.paneIndex] = minPercent;
  }

  if (opts.maxWidthPx) {
    const currentPx = percentToPx(sizes[opts.paneIndex], opts.containerWidth);

    if (currentPx > opts.maxWidthPx) {
      const maxPercent = pxToPercent(opts.maxWidthPx, opts.containerWidth);

      sizes[opts.contentIndex] += sizes[opts.paneIndex] - maxPercent;
      sizes[opts.paneIndex] = maxPercent;
    }
  }

  const minContentWidthPx =
    opts.minContentWidthPx == null
      ? undefined
      : Math.min(
          opts.minContentWidthPx,
          Math.max(0, opts.containerWidth - opts.minWidthPx - 48),
        );

  if (minContentWidthPx) {
    const contentPx = percentToPx(
      sizes[opts.contentIndex],
      opts.containerWidth,
    );

    if (contentPx < minContentWidthPx) {
      const minContentPercent = pxToPercent(
        minContentWidthPx,
        opts.containerWidth,
      );

      sizes[opts.paneIndex] -= minContentPercent - sizes[opts.contentIndex];
      sizes[opts.contentIndex] = minContentPercent;

      const paneAfter = percentToPx(sizes[opts.paneIndex], opts.containerWidth);

      if (paneAfter < opts.minWidthPx) {
        const minPercent = pxToPercent(opts.minWidthPx, opts.containerWidth);

        sizes[opts.contentIndex] -= minPercent - sizes[opts.paneIndex];
        sizes[opts.paneIndex] = minPercent;
      }
    }
  }
}

export function useCollapsiblePane({
  paneId,
  contentId,
  panePosition = 'start',
  minWidthPx,
  maxWidthPx,
  minContentWidthPx,
  preferredPaneWidthPx,
  collapsedWidthPx,
  snapThresholdPx,
  collapseOnSnap = true,
  persistedSize,
  isCollapsed,
  onCollapseChange,
  onResizeEnd,
}: UseCollapsiblePaneConfig) {
  const splitterRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const pendingRef = useRef<number[] | null>(null);
  const isDraggingRef = useRef(false);
  const isCollapsedRef = useRef(isCollapsed);

  isCollapsedRef.current = isCollapsed;

  const paneIndex = panePosition === 'start' ? 0 : 1;
  const contentIndex = panePosition === 'start' ? 1 : 0;

  const panels = useMemo(() => {
    const panePanel = { id: paneId };
    const contentPanel = { id: contentId };

    return panePosition === 'start'
      ? [panePanel, contentPanel]
      : [contentPanel, panePanel];
  }, [contentId, paneId, panePosition]);

  const panelKey = [paneId, contentId].join(':');

  const computeSizes = useCallback((): number[] => {
    const cw = splitterRef.current?.offsetWidth ?? FALLBACK_CONTAINER_WIDTH_PX;
    const collapsedPercent = pxToPercent(collapsedWidthPx, cw);

    let pane: number;

    if (isCollapsed) {
      pane = collapsedPercent;
    } else {
      const targetPx = preferredPaneWidthPx ?? percentToPx(persistedSize, cw);

      pane = pxToPercent(Math.max(targetPx, minWidthPx), cw);
    }

    const sizes =
      panePosition === 'start' ? [pane, 100 - pane] : [100 - pane, pane];

    if (!isCollapsed) {
      snapOrClampPane(sizes, {
        paneIndex,
        contentIndex,
        minWidthPx,
        maxWidthPx,
        minContentWidthPx,
        collapsedWidthPx,
        snapThresholdPx,
        containerWidth: cw,
        skipSnap: !collapseOnSnap,
      });
    }

    return sizes;
  }, [
    isCollapsed,
    collapsedWidthPx,
    persistedSize,
    panePosition,
    paneIndex,
    contentIndex,
    minWidthPx,
    maxWidthPx,
    minContentWidthPx,
    preferredPaneWidthPx,
    snapThresholdPx,
    collapseOnSnap,
  ]);

  const [controlledSizes, setControlledSizes] = useState(computeSizes);
  const prevPersistedSizeRef = useRef(persistedSize);
  const prevConstraintsRef = useRef({
    minWidthPx,
    maxWidthPx,
    minContentWidthPx,
  });

  useLayoutEffect(() => {
    const persistedSizeChanged = prevPersistedSizeRef.current !== persistedSize;
    const constraintsChanged =
      prevConstraintsRef.current.minWidthPx !== minWidthPx ||
      prevConstraintsRef.current.maxWidthPx !== maxWidthPx ||
      prevConstraintsRef.current.minContentWidthPx !== minContentWidthPx;

    prevPersistedSizeRef.current = persistedSize;
    prevConstraintsRef.current = {
      minWidthPx,
      maxWidthPx,
      minContentWidthPx,
    };

    setControlledSizes((prev) => {
      const cw =
        splitterRef.current?.offsetWidth ?? FALLBACK_CONTAINER_WIDTH_PX;

      if (isCollapsed) {
        const collapsedPercent = pxToPercent(collapsedWidthPx, cw);

        return panePosition === 'start'
          ? [collapsedPercent, 100 - collapsedPercent]
          : [100 - collapsedPercent, collapsedPercent];
      }

      const prevPanePx =
        prev.length === 2 ? percentToPx(prev[paneIndex], cw) : 0;
      const wasCollapsed = prevPanePx < snapThresholdPx;
      const shouldRecomputeFromPersisted =
        wasCollapsed || (persistedSizeChanged && !constraintsChanged);
      const next = shouldRecomputeFromPersisted ? computeSizes() : [...prev];

      snapOrClampPane(next, {
        paneIndex,
        contentIndex,
        minWidthPx,
        maxWidthPx,
        minContentWidthPx,
        collapsedWidthPx,
        snapThresholdPx,
        containerWidth: cw,
        skipSnap: !collapseOnSnap,
      });

      return next;
    });
  }, [
    computeSizes,
    isCollapsed,
    collapsedWidthPx,
    panePosition,
    paneIndex,
    contentIndex,
    minWidthPx,
    maxWidthPx,
    minContentWidthPx,
    snapThresholdPx,
    collapseOnSnap,
    persistedSize,
  ]);

  useEffect(() => {
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  useEffect(() => {
    const el = splitterRef.current;

    if (!el) return;

    const observer = new ResizeObserver(() => {
      if (rafRef.current !== null) return;

      const cw = el.offsetWidth;

      if (!cw) return;

      setControlledSizes((prev) => {
        const next = [...prev];

        if (isCollapsedRef.current) {
          const collapsedPercent = pxToPercent(collapsedWidthPx, cw);

          next[contentIndex] += next[paneIndex] - collapsedPercent;
          next[paneIndex] = collapsedPercent;
        } else {
          snapOrClampPane(next, {
            paneIndex,
            contentIndex,
            minWidthPx,
            maxWidthPx,
            minContentWidthPx,
            collapsedWidthPx,
            snapThresholdPx,
            containerWidth: cw,
            skipSnap: true,
          });
        }

        return next;
      });
    });

    observer.observe(el);

    return () => observer.disconnect();
  }, [
    paneIndex,
    contentIndex,
    collapsedWidthPx,
    minWidthPx,
    maxWidthPx,
    minContentWidthPx,
    snapThresholdPx,
  ]);

  const handleResize = useCallback(
    ({ size }: { size: number[] }) => {
      isDraggingRef.current = true;
      pendingRef.current = size;
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const raw = pendingRef.current;

        if (!raw) return;
        pendingRef.current = null;
        const cw =
          splitterRef.current?.offsetWidth ?? FALLBACK_CONTAINER_WIDTH_PX;
        const next = [...raw];

        snapOrClampPane(next, {
          paneIndex,
          contentIndex,
          minWidthPx,
          maxWidthPx,
          minContentWidthPx,
          collapsedWidthPx,
          snapThresholdPx,
          containerWidth: cw,
          skipSnap: !collapseOnSnap,
        });
        setControlledSizes(next);
      });
    },
    [
      paneIndex,
      contentIndex,
      minWidthPx,
      maxWidthPx,
      minContentWidthPx,
      collapsedWidthPx,
      snapThresholdPx,
      collapseOnSnap,
    ],
  );

  const handleResizeEnd = useCallback(
    ({ size }: { size: number[] }) => {
      isDraggingRef.current = false;
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }

      pendingRef.current = null;
      const cw =
        splitterRef.current?.offsetWidth ?? FALLBACK_CONTAINER_WIDTH_PX;
      const final = [...size];

      snapOrClampPane(final, {
        paneIndex,
        contentIndex,
        minWidthPx,
        maxWidthPx,
        minContentWidthPx,
        collapsedWidthPx,
        snapThresholdPx,
        containerWidth: cw,
        skipSnap: !collapseOnSnap,
      });
      const panePx = percentToPx(final[paneIndex], cw);
      const shouldCollapse = collapseOnSnap && panePx < snapThresholdPx;

      setControlledSizes(final);

      onCollapseChange(shouldCollapse);
      if (!shouldCollapse) {
        onResizeEnd(final[paneIndex], final[contentIndex], false);
      }
    },
    [
      paneIndex,
      contentIndex,
      minWidthPx,
      maxWidthPx,
      minContentWidthPx,
      snapThresholdPx,
      collapseOnSnap,
      collapsedWidthPx,
      onCollapseChange,
      onResizeEnd,
    ],
  );

  const toggle = useCallback(() => {
    onCollapseChange(!isCollapsed);
  }, [isCollapsed, onCollapseChange]);

  const measuredContainerWidth = splitterRef.current?.offsetWidth;
  const isVisuallyCollapsed = isDraggingRef.current
    ? percentToPx(
        controlledSizes[paneIndex] ?? 0,
        measuredContainerWidth ?? FALLBACK_CONTAINER_WIDTH_PX,
      ) < snapThresholdPx
    : measuredContainerWidth
      ? percentToPx(controlledSizes[paneIndex] ?? 0, measuredContainerWidth) <
        snapThresholdPx
      : isCollapsed;

  const collapsedClassName = useMemo(
    () =>
      isVisuallyCollapsed
        ? css({
            minWidth: `${collapsedWidthPx}px`,
            maxWidth: `${collapsedWidthPx}px`,
          })
        : undefined,
    [isVisuallyCollapsed, collapsedWidthPx],
  );

  return {
    splitterRef,
    panels,
    panelKey,
    controlledSizes,
    handleResize,
    handleResizeEnd,
    toggle,
    isCollapsed: isVisuallyCollapsed,
    collapsedClassName,
  };
}
